import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useSlideOperations } from "../../context/InsertedSlidesContext";
import { useSendMessage } from "../../../../hooks/useSendMessage";
import { useConversation } from "../../../../context/ConversationContext";
import { AIPrimaryButton } from "../../../../components/common/AIPrimaryButton";
import { ConfirmationDialog } from "../../../../components/common";
import SectionList from "./components/SectionList";
import { useSelection } from "../../context/SelectionContext";
import { useDragDrop } from "../../context/DragDropContext";
import { useEdit } from "../../context/EditContext";
import { useChangeTracking } from "../../context/ChangeTrackingContext";
import { usePlanHelpers } from "../../hooks/usePlanHelpers";
const PresentationPlanView = ({ plan, planDescription, expandedSections, onToggleSection, onPlanUpdate, onClearSectionTrackingRef, onStartBuildImmediately, onQueueBuild, onBuildPlan, isBuildingPlan = false, currentPlanId, }) => {
    const { addSlideOperation, addSectionOperation, slideOperations, sectionOperations, clearSlideOperations, clearSectionOperations, clearAllOperations } = useSlideOperations();
    // Use the centralized message sending hook instead of PlanningLLMService
    // Use sendSilentMessage for inline forms so it doesn't add to chat UI
    const { sendSilentMessage } = useSendMessage();
    const { isLoading: isConversationLoading, conversationId } = useConversation();
    // Track pending inline operations (for resetting UI state when complete)
    const [pendingEditOperation, setPendingEditOperation] = useState(null);
    // Confirmation dialog state
    const [confirmDialog, setConfirmDialog] = useState(null);
    // Use context hooks instead of useState
    const { selectedSlides, selectedSections, isSelecting, selectionStart, selectionEnd, editingSelection, editSelectionPrompt, processingEditSelection, setSelectedSlides, setSelectedSections, setIsSelecting, setSelectionStart, setSelectionEnd, setEditingSelection, setEditSelectionPrompt, setProcessingEditSelection, } = useSelection();
    const { draggedSectionId, dragOverSectionId, dragOverSectionDropZone, draggedSlideId, dragOverDropZone, hoveredSlideId, originalSectionOrder, originalSlideOrders, setDraggedSectionId, setDragOverSectionId, setDragOverSectionDropZone, setDraggedSlideId, setDragOverDropZone, setHoveredSlideId, setOriginalSectionOrder, setOriginalSlideOrders, } = useDragDrop();
    const { editingSlideId, editSlideTitle, editSlidePurpose, addingAtPosition, newSlideTitle, newSlidePurpose, addingSectionAfterIndex, creatingSectionAtIndex, newSectionDescription, setEditingSlideId, setEditSlideTitle, setEditSlidePurpose, setAddingAtPosition, setNewSlideTitle, setNewSlidePurpose, setAddingSectionAfterIndex, setCreatingSectionAtIndex, setNewSectionDescription, startEditSlide, cancelEditSlide, startAddSlide, cancelAddSlide, startAddSection, cancelAddSection, stopSectionCreation, } = useEdit();
    const { updatedSectionIds, setUpdatedSectionIds } = useChangeTracking();
    const { findSlide, findSection, hasSlideChanges, isSlideDeleted, isSlideReordered, getSlideOperation, isSectionReordered, isSectionDeleted, isFirstSelectedSlideDivider: isFirstSelectedSlideDividerHelper, } = usePlanHelpers(plan);
    // Watch for plan changes to reset pending operation UI states
    // When the plan updates (from backend via window.__registerPlanUpdateCallback),
    // we reset the UI state for any pending inline operations
    // Uses multiple signals to detect change: version, sections count, or lastModified
    useEffect(() => {
        if (plan && pendingEditOperation) {
            const currentVersion = plan.version || 0;
            const currentSectionsCount = plan.sections?.length || 0;
            const currentLastModified = plan.metadata?.lastModified || 0;
            const startVersion = pendingEditOperation.planVersionAtStart;
            const startSectionsCount = pendingEditOperation.planSectionsCountAtStart;
            const startLastModified = pendingEditOperation.planLastModifiedAtStart;
            // Check if plan has actually changed using any of these signals
            const versionChanged = currentVersion > startVersion;
            const sectionsCountChanged = currentSectionsCount !== startSectionsCount;
            const lastModifiedChanged = currentLastModified > startLastModified;
            const planHasChanged = versionChanged || sectionsCountChanged || lastModifiedChanged;
            if (planHasChanged) {
                console.log('[PresentationPlanView] Plan changed - resetting pending operation state', {
                    versionChanged: versionChanged ? `${startVersion} → ${currentVersion}` : 'no',
                    sectionsCountChanged: sectionsCountChanged ? `${startSectionsCount} → ${currentSectionsCount}` : 'no',
                    lastModifiedChanged: lastModifiedChanged ? `${startLastModified} → ${currentLastModified}` : 'no',
                });
                // Reset UI state based on operation type
                if (pendingEditOperation.type === 'edit_selection') {
                    setEditingSelection(false);
                    setEditSelectionPrompt("");
                    setProcessingEditSelection(false);
                    setSelectedSlides(new Set());
                    setSelectedSections(new Set());
                }
                else if (pendingEditOperation.type === 'create_section') {
                    setCreatingSectionAtIndex(null);
                    setAddingSectionAfterIndex(null);
                    setNewSectionDescription("");
                }
                // Clear the pending operation
                setPendingEditOperation(null);
            }
        }
    }, [plan, pendingEditOperation, setEditingSelection, setEditSelectionPrompt, setProcessingEditSelection, setSelectedSlides, setSelectedSections, setCreatingSectionAtIndex, setAddingSectionAfterIndex, setNewSectionDescription]);
    // Helper to track changes from flags in the updated plan (LLM-initiated changes)
    // These have source: 'llm' so they show in ChangeTracker with accept/reject
    const trackChangesFromFlags = (updatedPlan) => {
        console.log('[PresentationPlanView] trackChangesFromFlags called with plan:', updatedPlan);
        // Track section changes - convert to slide operations
        updatedPlan.sections.forEach((section, sectionIndex) => {
            if (section._pendingDeletion) {
                console.log('[PresentationPlanView] Tracking deleted section:', section.name);
                // Track all slides in the deleted section as deleted instead of tracking the section
                let slideNumber = 0;
                for (let i = 0; i < sectionIndex; i++) {
                    slideNumber += updatedPlan.sections[i].slides.length;
                }
                section.slides.forEach((slide, slideIndex) => {
                    addSlideOperation({
                        operation: 'deleted',
                        slideId: slide.id,
                        slideName: slide.name,
                        sectionName: section.name,
                        slideNumber: slideNumber + slideIndex + 1,
                        timestamp: Date.now(),
                        source: 'llm', // LLM-initiated: shows in ChangeTracker with accept/reject
                    });
                });
            }
            else if (section._pendingInsertion) {
                // Track slides in newly added sections
                console.log('[PresentationPlanView] Tracking newly added section:', section.name);
                let slideNumber = 0;
                for (let i = 0; i < sectionIndex; i++) {
                    slideNumber += updatedPlan.sections[i].slides.length;
                }
                section.slides.forEach((slide, slideIndex) => {
                    console.log('[PresentationPlanView] Tracking inserted slide in new section:', slide.id, slide.name);
                    addSlideOperation({
                        operation: 'inserted',
                        slideId: slide.id,
                        slideName: slide.name,
                        sectionName: section.name,
                        slideNumber: slideNumber + slideIndex + 1,
                        timestamp: Date.now(),
                        source: 'llm', // LLM-initiated: shows in ChangeTracker with accept/reject
                    });
                });
                // Skip the second loop for newly added sections (already tracked all slides above)
                return;
            }
            // ALWAYS check all slides in ALL sections for flags (not just when section has flags)
            // This ensures slides added to existing sections are tracked
            section.slides.forEach((slide, slideIndex) => {
                if (slide._pendingUpdate || slide._pendingDeletion || slide._pendingInsertion) {
                    console.log('[PresentationPlanView] Found slide with flag:', slide.id, slide.name, {
                        _pendingUpdate: slide._pendingUpdate,
                        _pendingDeletion: slide._pendingDeletion,
                        _pendingInsertion: slide._pendingInsertion,
                    });
                    // Calculate slide number
                    let slideNumber = 0;
                    for (let i = 0; i < sectionIndex; i++) {
                        slideNumber += updatedPlan.sections[i].slides.length;
                    }
                    slideNumber += slideIndex + 1;
                    if (slide._pendingDeletion) {
                        console.log('[PresentationPlanView] Tracking deleted slide:', slide.id, slide.name);
                        addSlideOperation({
                            operation: 'deleted',
                            slideId: slide.id,
                            slideName: slide.name,
                            sectionName: section.name,
                            slideNumber: slideNumber,
                            timestamp: Date.now(),
                            source: 'llm', // LLM-initiated: shows in ChangeTracker with accept/reject
                        });
                    }
                    else if (slide._pendingInsertion) {
                        console.log('[PresentationPlanView] Tracking inserted slide:', slide.id, slide.name);
                        addSlideOperation({
                            operation: 'inserted',
                            slideId: slide.id,
                            slideName: slide.name,
                            sectionName: section.name,
                            slideNumber: slideNumber,
                            timestamp: Date.now(),
                            source: 'llm', // LLM-initiated: shows in ChangeTracker with accept/reject
                        });
                    }
                    else if (slide._pendingUpdate) {
                        console.log('[PresentationPlanView] Tracking updated slide:', slide.id, slide.name);
                        addSlideOperation({
                            operation: 'updated',
                            slideId: slide.id,
                            slideName: slide.name,
                            sectionName: section.name,
                            slideNumber: slideNumber,
                            timestamp: Date.now(),
                            source: 'llm', // LLM-initiated: shows in ChangeTracker with accept/reject
                        });
                    }
                }
            });
        });
        console.log('[PresentationPlanView] Finished tracking changes from flags');
    };
    // Track changes from flags when plan prop changes (e.g., when LLM updates plan from DetailedPlanning)
    // This ensures that when the plan is updated from outside this component, changes are still tracked
    const previousPlanIdRef = useRef(null);
    useEffect(() => {
        if (plan) {
            // Use plan version or id to detect if it's a new plan update
            const planId = plan.id + '_' + (plan.version || 0);
            // Only track if this is a new plan version (not the same plan we already processed)
            if (previousPlanIdRef.current !== planId) {
                // Check if plan has any flags indicating changes
                const hasFlags = plan.sections.some(section => section._pendingDeletion || section._pendingUpdate || section._pendingInsertion ||
                    section.slides.some(slide => slide._pendingDeletion || slide._pendingUpdate || slide._pendingInsertion));
                // Don't re-track if this plan update came from an accept/reject action
                const isFromAcceptReject = lastAcceptRejectPlanId.current === planId;
                if (hasFlags && !isFromAcceptReject) {
                    console.log('[PresentationPlanView] Plan prop changed with flags, tracking changes');
                    trackChangesFromFlags(plan);
                    // Mark sections with pending changes
                    const changedSectionIds = new Set();
                    plan.sections.forEach(section => {
                        if (section._pendingDeletion || section._pendingUpdate || section._pendingInsertion) {
                            changedSectionIds.add(section.id);
                        }
                    });
                    if (changedSectionIds.size > 0) {
                        setUpdatedSectionIds(prev => new Set([...prev, ...changedSectionIds]));
                    }
                }
                previousPlanIdRef.current = planId;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plan]);
    // Helper to get all changes for a specific section
    const getSectionChanges = (sectionId) => {
        if (!plan)
            return [];
        const section = plan.sections.find(s => s.id === sectionId);
        if (!section)
            return [];
        // Get all slide IDs in this section
        const sectionSlideIds = new Set(section.slides.map(s => s.id));
        // Filter operations that belong to slides in this section
        return slideOperations.filter(op => sectionSlideIds.has(op.slideId));
    };
    // Store original order when component mounts or plan changes
    useEffect(() => {
        if (!plan)
            return;
        const sectionOrderMap = new Map();
        const slideOrderMap = new Map();
        plan.sections.forEach((section, sectionIndex) => {
            sectionOrderMap.set(section.id, sectionIndex);
            section.slides.forEach((slide, slideIndex) => {
                slideOrderMap.set(slide.id, { sectionId: section.id, order: slideIndex });
            });
        });
        setOriginalSectionOrder(sectionOrderMap);
        setOriginalSlideOrders(slideOrderMap);
    }, [plan?.id]); // Only reset when plan ID changes (new plan), not on every update
    // Helper to clean up flags from a plan
    const cleanupFlags = (planToClean, sectionId, slideIds) => {
        const cleanedPlan = {
            ...planToClean,
            sections: planToClean.sections
                .filter(section => {
                // If sectionId specified and section is deleted, remove it
                if (sectionId && section.id === sectionId && section._pendingDeletion) {
                    return false;
                }
                return true;
            })
                .map(section => {
                // If sectionId specified, only clean that section
                if (sectionId && section.id !== sectionId) {
                    return section;
                }
                // Clean section flags
                const { _pendingDeletion, _pendingUpdate, _pendingInsertion, ...cleanedSection } = section;
                // Clean slide flags
                const cleanedSlides = section.slides
                    .filter(slide => {
                    // If slideIds specified, only process those slides
                    if (slideIds && !slideIds.has(slide.id)) {
                        return true; // Keep slide, don't filter
                    }
                    // Remove slides marked for deletion
                    return !slide._pendingDeletion;
                })
                    .map(slide => {
                    // If slideIds specified, only clean those slides
                    if (slideIds && !slideIds.has(slide.id)) {
                        return slide;
                    }
                    const { _pendingDeletion, _pendingUpdate, _pendingInsertion, ...cleanedSlide } = slide;
                    return cleanedSlide;
                });
                return {
                    ...cleanedSection,
                    slides: cleanedSlides,
                };
            }),
        };
        // Recalculate slide count
        cleanedPlan.slideCount = cleanedPlan.sections.reduce((sum, s) => sum + s.slides.length, 0);
        cleanedPlan.metadata.lastModified = Date.now();
        return cleanedPlan;
    };
    // Handle accepting all changes in a section
    // Memoized to prevent unnecessary re-renders when passed to children
    const handleAcceptSectionChanges = useCallback((sectionId) => {
        if (!plan)
            return;
        const section = findSection(sectionId);
        if (!section)
            return;
        const isDeletedSection = section._pendingDeletion === true;
        // If section is deleted, remove it from the plan and clean up flags
        if (isDeletedSection) {
            const updatedPlan = cleanupFlags(plan, sectionId);
            // Mark this plan update as coming from accept action to prevent re-tracking
            lastAcceptRejectPlanId.current = plan.id + '_' + (plan.version || 0);
            if (onPlanUpdate)
                onPlanUpdate(updatedPlan);
            // Clear section operation from tracking
            const remainingSectionOps = sectionOperations.filter(op => op.sectionId !== sectionId);
            clearSectionOperations();
            remainingSectionOps.forEach(op => addSectionOperation(op));
            return;
        }
        // Get slide IDs in this section that have flags
        const slideIdsToClean = new Set();
        section.slides.forEach(slide => {
            if (slide._pendingDeletion || slide._pendingUpdate || slide._pendingInsertion) {
                slideIdsToClean.add(slide.id);
            }
        });
        // Clean up flags for this section and its slides
        const updatedPlan = cleanupFlags(plan, sectionId, slideIdsToClean);
        // Mark this plan update as coming from accept action to prevent re-tracking
        lastAcceptRejectPlanId.current = plan.id + '_' + (plan.version || 0);
        if (onPlanUpdate)
            onPlanUpdate(updatedPlan);
        // Clear tracking operations for slides in this section
        const changes = getSectionChanges(sectionId);
        const changesIds = new Set(changes.map(op => op.slideId));
        const remainingOps = slideOperations.filter(op => !changesIds.has(op.slideId));
        clearSlideOperations();
        remainingOps.forEach(op => addSlideOperation(op));
        // Remove from updatedSectionIds
        if (updatedSectionIds.has(sectionId)) {
            setUpdatedSectionIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(sectionId);
                return newSet;
            });
        }
        // Clear original positions for all slides in this section (do it in one update)
        const slideIdsInSection = section.slides.map(slide => slide.id);
        setOriginalSlideOrders(prev => {
            const newMap = new Map(prev);
            slideIdsInSection.forEach(slideId => newMap.delete(slideId));
            return newMap;
        });
        // Clear original position for the section itself
        setOriginalSectionOrder(prev => {
            const newMap = new Map(prev);
            newMap.delete(sectionId);
            return newMap;
        });
        // Clear section operations for this section
        const remainingSectionOps = sectionOperations.filter(op => op.sectionId !== sectionId);
        clearSectionOperations();
        remainingSectionOps.forEach(op => addSectionOperation(op));
    }, [plan, findSection, cleanupFlags, onPlanUpdate, sectionOperations, clearSectionOperations, addSectionOperation, getSectionChanges, slideOperations, clearSlideOperations, addSlideOperation, updatedSectionIds, setUpdatedSectionIds, setOriginalSlideOrders, setOriginalSectionOrder]);
    // Handle rejecting all changes in a section
    const handleRejectSectionChanges = useCallback((sectionId) => {
        if (!plan)
            return;
        const section = findSection(sectionId);
        if (!section)
            return;
        const isDeletedSection = section._pendingDeletion === true;
        const isNewlyAddedSection = section._pendingInsertion === true;
        // Calculate section position
        const sectionPosition = plan.sections.findIndex(s => s.id === sectionId);
        // If section is deleted, just clear the flag (section stays in plan) - user rejected LLM's deletion
        if (isDeletedSection) {
            // Track: User rejected LLM's section deletion (user wants to keep the section)
            addSectionOperation({
                operation: 'rejected_deletion',
                sectionId: sectionId,
                sectionName: section.name,
                timestamp: Date.now(),
                source: 'ui',
                rejectedOperation: 'deleted',
                position: sectionPosition,
            });
            const updatedPlan = {
                ...plan,
                sections: plan.sections.map(s => s.id === sectionId
                    ? { ...s, _pendingDeletion: undefined }
                    : s),
                metadata: plan.metadata, // Don't update lastModified during reject
            };
            // Mark this plan update as coming from reject action to prevent re-tracking
            lastAcceptRejectPlanId.current = plan.id + '_' + (plan.version || 0);
            if (onPlanUpdate)
                onPlanUpdate(updatedPlan);
            // Clear section operation from tracking (remove the LLM's deleted operation)
            const remainingSectionOps = sectionOperations.filter(op => !(op.sectionId === sectionId && op.operation === 'deleted'));
            clearSectionOperations();
            remainingSectionOps.forEach(op => addSectionOperation(op));
            return;
        }
        // If section is newly added, remove it - user rejected LLM's addition
        if (isNewlyAddedSection) {
            // Track: User rejected LLM's section insertion
            addSectionOperation({
                operation: 'rejected_insertion',
                sectionId: sectionId,
                sectionName: section.name,
                timestamp: Date.now(),
                source: 'ui',
                rejectedOperation: 'added',
                position: sectionPosition,
            });
            // IMPORTANT: Get all slide IDs from this section BEFORE removing it
            // so we can clear their operations from tracking
            const sectionSlideIds = new Set(section.slides.map(slide => slide.id));
            const updatedPlan = {
                ...plan,
                sections: plan.sections.filter(s => s.id !== sectionId),
                metadata: plan.metadata, // Don't update lastModified during reject
            };
            updatedPlan.slideCount = updatedPlan.sections.reduce((sum, s) => sum + s.slides.length, 0);
            // Mark this plan update as coming from reject action to prevent re-tracking
            lastAcceptRejectPlanId.current = plan.id + '_' + (plan.version || 0);
            if (onPlanUpdate) {
                onPlanUpdate(updatedPlan);
            }
            // Remove from updatedSectionIds
            setUpdatedSectionIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(sectionId);
                return newSet;
            });
            // Clear slide operations for all slides in this section (remove LLM's inserted ops)
            const remainingSlideOps = slideOperations.filter(op => !sectionSlideIds.has(op.slideId));
            clearSlideOperations();
            remainingSlideOps.forEach(op => addSlideOperation(op));
            // Clear section operation from tracking (remove LLM's added operation)
            const remainingSectionOps = sectionOperations.filter(op => !(op.sectionId === sectionId && op.operation === 'added'));
            clearSectionOperations();
            remainingSectionOps.forEach(op => addSectionOperation(op));
            console.log('[PresentationPlanView] Rejected newly added section:', {
                sectionId,
                sectionName: section.name,
                slideIdsRemoved: Array.from(sectionSlideIds),
                slideOpsRemoved: slideOperations.filter(op => sectionSlideIds.has(op.slideId)).length,
                remainingSlideOps: remainingSlideOps.length
            });
            return;
        }
        // Check for reorder operation and revert - user rejected LLM's reorder
        const reorderOp = sectionOperations.find(op => op.sectionId === sectionId && op.operation === 'reordered');
        if (reorderOp && reorderOp.previousPosition !== undefined) {
            // Track: User rejected LLM's section reorder
            addSectionOperation({
                operation: 'rejected_reorder',
                sectionId: sectionId,
                sectionName: section.name,
                timestamp: Date.now(),
                source: 'ui',
                rejectedOperation: 'reordered',
                previousPosition: reorderOp.previousPosition,
                newPosition: reorderOp.newPosition,
            });
            const currentPos = plan.sections.findIndex(s => s.id === sectionId);
            if (currentPos !== -1 && currentPos !== reorderOp.previousPosition) {
                const newSections = [...plan.sections];
                const [movedSection] = newSections.splice(currentPos, 1);
                newSections.splice(reorderOp.previousPosition, 0, movedSection);
                const updatedPlan = {
                    ...plan,
                    sections: newSections,
                    metadata: plan.metadata, // Don't update lastModified during reject
                };
                // Mark this plan update as coming from reject action to prevent re-tracking
                lastAcceptRejectPlanId.current = plan.id + '_' + (plan.version || 0);
                if (onPlanUpdate)
                    onPlanUpdate(updatedPlan);
                // Clear reorder operation from tracking
                const remainingSectionOps = sectionOperations.filter(op => !(op.sectionId === sectionId && op.operation === 'reordered'));
                clearSectionOperations();
                remainingSectionOps.forEach(op => addSectionOperation(op));
                // Also clear reordered slide operations for this section
                const remainingSlideOps = slideOperations.filter(op => op.sectionName !== section.name || op.operation !== 'reordered');
                clearSlideOperations();
                remainingSlideOps.forEach(op => addSlideOperation(op));
                return;
            }
        }
        // Reject slide changes: remove inserted slides, clear flags for updated/deleted slides
        // IMPORTANT: Get all slide IDs in this section BEFORE updating plan
        const sectionSlideIds = new Set(section.slides.map(slide => slide.id));
        const slideIdsToReject = new Set();
        section.slides.forEach(slide => {
            if (slide._pendingDeletion || slide._pendingUpdate || slide._pendingInsertion) {
                slideIdsToReject.add(slide.id);
            }
        });
        // Get all operations for slides currently in this section (by slide ID, not section name)
        // This catches all operations including those for inserted slides that will be removed
        // and slides that may have been moved between sections
        const sectionSlideOps = slideOperations.filter(op => sectionSlideIds.has(op.slideId));
        const sectionSlideOpIds = new Set(sectionSlideOps.map(op => op.slideId));
        console.log('[PresentationPlanView] Rejecting section changes:', {
            sectionId,
            sectionName: section.name,
            sectionSlideIds: Array.from(sectionSlideIds),
            sectionSlideOps: sectionSlideOps.map(op => ({ slideId: op.slideId, operation: op.operation, slideName: op.slideName })),
            totalOperationsBefore: slideOperations.length,
            operationsToRemove: sectionSlideOps.length
        });
        let updatedPlan = {
            ...plan,
            sections: plan.sections.map(s => {
                if (s.id !== sectionId)
                    return s;
                return {
                    ...s,
                    slides: s.slides
                        .filter(slide => {
                        // Remove inserted slides
                        if (slide._pendingInsertion) {
                            return false;
                        }
                        return true;
                    })
                        .map(slide => {
                        // Clear flags for updated/deleted slides
                        if (slideIdsToReject.has(slide.id)) {
                            const { _pendingDeletion, _pendingUpdate, _pendingInsertion, ...cleanedSlide } = slide;
                            return cleanedSlide;
                        }
                        return slide;
                    }),
                };
            }),
            metadata: plan.metadata, // Don't update lastModified during reject
        };
        updatedPlan.slideCount = updatedPlan.sections.reduce((sum, s) => sum + s.slides.length, 0);
        // Mark this plan update as coming from reject action to prevent re-tracking
        lastAcceptRejectPlanId.current = plan.id + '_' + (plan.version || 0);
        if (onPlanUpdate)
            onPlanUpdate(updatedPlan);
        // 2. Update tracking - filter out all operations for slides in this section
        // Use the Set we created BEFORE updating the plan to ensure we catch all operations
        const remainingOps = slideOperations.filter(op => !sectionSlideOpIds.has(op.slideId));
        console.log('[PresentationPlanView] After filtering:', {
            remainingOpsCount: remainingOps.length,
            removedOpsCount: slideOperations.length - remainingOps.length
        });
        clearSlideOperations();
        remainingOps.forEach(op => addSlideOperation(op));
        // Also remove from updatedSectionIds if it was there
        if (updatedSectionIds.has(sectionId)) {
            setUpdatedSectionIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(sectionId);
                return newSet;
            });
        }
        // Clear original positions for all slides in this section (do it in one update)
        const slideIdsInSection = section.slides.map(slide => slide.id);
        setOriginalSlideOrders(prev => {
            const newMap = new Map(prev);
            slideIdsInSection.forEach(slideId => newMap.delete(slideId));
            return newMap;
        });
        // Clear original position for the section itself
        setOriginalSectionOrder(prev => {
            const newMap = new Map(prev);
            newMap.delete(sectionId);
            return newMap;
        });
        // Clear section operations for this section
        const remainingSectionOps = sectionOperations.filter(op => op.sectionId !== sectionId);
        clearSectionOperations();
        remainingSectionOps.forEach(op => addSectionOperation(op));
    }, [plan, findSection, onPlanUpdate, setUpdatedSectionIds, slideOperations, clearSlideOperations, addSlideOperation, sectionOperations, clearSectionOperations, addSectionOperation, updatedSectionIds, setOriginalSlideOrders, setOriginalSectionOrder]);
    // Drag-and-drop handlers for sections
    // Memoized to prevent unnecessary re-renders of child components
    const handleSectionDragStart = useCallback((e, sectionId) => {
        if (!plan)
            return;
        // Store original position
        const sectionIndex = plan.sections.findIndex(s => s.id === sectionId);
        if (sectionIndex !== -1) {
            setOriginalSectionOrder(prev => {
                const newMap = new Map(prev);
                newMap.set(sectionId, sectionIndex);
                return newMap;
            });
        }
        setDraggedSectionId(sectionId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', ''); // Required for Firefox
    }, [plan, setOriginalSectionOrder, setDraggedSectionId]);
    const handleSectionDragEnd = useCallback(() => {
        setDraggedSectionId(null);
        setDragOverSectionId(null);
        setDragOverSectionDropZone(null);
    }, [setDraggedSectionId, setDragOverSectionId, setDragOverSectionDropZone]);
    const handleSectionDragOver = useCallback((e, sectionId) => {
        // Prevent default drag over on section itself - we'll handle drop zones instead
        // Don't prevent default or stop propagation - let it bubble to drop zones
        if (draggedSectionId && draggedSectionId !== sectionId) {
            e.dataTransfer.dropEffect = 'none'; // Don't allow drop on section itself
        }
    }, [draggedSectionId]);
    // Handle drag over drop zone between sections
    const handleSectionDropZoneDragOver = useCallback((e, insertIndex) => {
        if (draggedSectionId) {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'move';
            console.log('[PresentationPlanView] Section drop zone drag over:', insertIndex);
            setDragOverSectionDropZone(insertIndex);
        }
    }, [draggedSectionId, setDragOverSectionDropZone]);
    const handleSectionDropZoneDragLeave = useCallback((e) => {
        // Only clear if we're actually leaving the drop zone (not entering a child)
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;
        if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            // Delay clearing to avoid flickering
            setTimeout(() => {
                setDragOverSectionDropZone(null);
            }, 50);
        }
    }, [setDragOverSectionDropZone]);
    const handleSectionDropZoneDrop = useCallback((e, insertIndex) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('[PresentationPlanView] Section drop zone drop:', { insertIndex, draggedSectionId });
        if (!plan || !draggedSectionId) {
            console.log('[PresentationPlanView] No plan or draggedSectionId');
            setDraggedSectionId(null);
            setDragOverSectionDropZone(null);
            return;
        }
        const sourceIndex = plan.sections.findIndex(s => s.id === draggedSectionId);
        if (sourceIndex === -1) {
            console.log('[PresentationPlanView] Source section not found');
            setDraggedSectionId(null);
            setDragOverSectionDropZone(null);
            return;
        }
        // Get original position
        const originalPos = originalSectionOrder.get(draggedSectionId) ?? sourceIndex;
        // Adjust insert index if dragging from before the target position
        // If dragging from index 1 to index 3, after removal we want to insert at 2 (3-1)
        let adjustedInsertIndex = insertIndex;
        if (sourceIndex < insertIndex) {
            adjustedInsertIndex = insertIndex - 1;
        }
        const newPos = adjustedInsertIndex;
        console.log('[PresentationPlanView] Section reorder:', { sourceIndex, insertIndex, adjustedInsertIndex, newPos, originalPos });
        // Reorder sections
        const newSections = [...plan.sections];
        const [movedSection] = newSections.splice(sourceIndex, 1);
        newSections.splice(newPos, 0, movedSection);
        const updatedPlan = {
            ...plan,
            sections: newSections,
            metadata: {
                ...plan.metadata,
                lastModified: Date.now(),
            },
        };
        // Track reordering operation - track all slides in the section instead of the section itself
        if (originalPos !== newPos) {
            // Calculate absolute slide numbers for all slides in the moved section
            let originalAbsoluteSlideNumber = 0;
            for (let i = 0; i < originalPos; i++) {
                originalAbsoluteSlideNumber += plan.sections[i].slides.length;
            }
            let newAbsoluteSlideNumber = 0;
            for (let i = 0; i < newPos; i++) {
                newAbsoluteSlideNumber += updatedPlan.sections[i].slides.length;
            }
            // Track section reorder for UI styling and reversion (UI-initiated = source: 'ui')
            addSectionOperation({
                operation: 'reordered',
                sectionId: movedSection.id,
                sectionName: movedSection.name,
                timestamp: Date.now(),
                previousPosition: originalPos,
                newPosition: newPos,
                source: 'ui', // UI-initiated: only for automated reminder, not shown in ChangeTracker
            });
            // Track each slide in the section as reordered (UI-initiated = source: 'ui')
            movedSection.slides.forEach((slide, slideIndex) => {
                const slideOriginalPos = originalAbsoluteSlideNumber + slideIndex;
                const slideNewPos = newAbsoluteSlideNumber + slideIndex;
                addSlideOperation({
                    operation: 'reordered',
                    slideId: slide.id,
                    slideName: slide.name,
                    sectionName: movedSection.name,
                    slideNumber: slideNewPos + 1, // 1-indexed for display
                    timestamp: Date.now(),
                    previousPosition: slideOriginalPos,
                    newPosition: slideNewPos,
                    source: 'ui', // UI-initiated: only for automated reminder, not shown in ChangeTracker
                });
            });
            // Section reorder uses sectionOperations for yellow styling, NOT updatedSectionIds
        }
        if (onPlanUpdate) {
            onPlanUpdate(updatedPlan);
        }
        setDraggedSectionId(null);
        setDragOverSectionDropZone(null);
    }, [plan, draggedSectionId, originalSectionOrder, addSectionOperation, addSlideOperation, onPlanUpdate, setDraggedSectionId, setDragOverSectionDropZone]);
    // Drag-and-drop handlers for slides
    const handleSlideDragStart = useCallback((e, slideId, sectionId) => {
        // Don't start drag if clicking on buttons or interactive elements
        const target = e.target;
        if (target.closest('button') || target.closest('input') || target.closest('textarea')) {
            e.preventDefault();
            return;
        }
        if (!plan)
            return;
        // Find the slide's current position
        let slideOrder = -1;
        for (let i = 0; i < plan.sections.length; i++) {
            const section = plan.sections[i];
            const slideIndex = section.slides.findIndex(s => s.id === slideId);
            if (slideIndex !== -1) {
                slideOrder = slideIndex;
                break;
            }
        }
        // Store original position
        if (slideOrder !== -1) {
            setOriginalSlideOrders(prev => {
                const newMap = new Map(prev);
                newMap.set(slideId, { sectionId, order: slideOrder });
                return newMap;
            });
        }
        setDraggedSlideId(slideId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', ''); // Required for Firefox
        e.dataTransfer.setData('sectionId', sectionId);
        e.stopPropagation();
    }, [plan, setOriginalSlideOrders, setDraggedSlideId]);
    const handleSlideDragEnd = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDraggedSlideId(null);
        setDragOverSectionId(null);
        setDragOverDropZone(null);
    }, [setDraggedSlideId, setDragOverSectionId, setDragOverDropZone]);
    const handleSlideDragOver = useCallback((_e, _slideId, _sectionId) => {
        // This is for dragging over slides themselves - we'll disable this
        // Drop zones will handle drag over
    }, []);
    // Handle drag over drop zone (between slides)
    const handleDropZoneDragOver = useCallback((e, sectionId, insertIndex) => {
        if (draggedSlideId) {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'move';
            setDragOverDropZone({ sectionId, insertIndex });
        }
    }, [draggedSlideId, setDragOverDropZone]);
    const handleDropZoneDragLeave = useCallback(() => {
        // Only clear if we're not moving to another drop zone
        setTimeout(() => {
            setDragOverDropZone(null);
        }, 50);
    }, [setDragOverDropZone]);
    // Handle drop on drop zone (between slides)
    const handleDropZoneDrop = useCallback((e, sectionId, insertIndex) => {
        e.preventDefault();
        e.stopPropagation();
        if (!plan || !draggedSlideId) {
            setDraggedSlideId(null);
            setDragOverDropZone(null);
            return;
        }
        // Find source slide and section
        let sourceSlide = null;
        let sourceSectionId = '';
        let sourceSlideIndex = -1;
        let sourceSectionIndex = -1;
        for (let i = 0; i < plan.sections.length; i++) {
            const slideIndex = plan.sections[i].slides.findIndex(s => s.id === draggedSlideId);
            if (slideIndex !== -1) {
                sourceSectionIndex = i;
                sourceSectionId = plan.sections[i].id;
                sourceSlideIndex = slideIndex;
                sourceSlide = plan.sections[i].slides[slideIndex];
                break;
            }
        }
        if (!sourceSlide) {
            setDraggedSlideId(null);
            setDragOverDropZone(null);
            return;
        }
        // Get original position
        const originalPos = originalSlideOrders.get(draggedSlideId);
        const targetSection = plan.sections.find(s => s.id === sectionId);
        if (!targetSection) {
            setDraggedSlideId(null);
            setDragOverDropZone(null);
            return;
        }
        // Calculate new position - account for removal if same section
        let newPos = insertIndex;
        if (sourceSectionId === sectionId) {
            // Moving within same section
            if (sourceSlideIndex < insertIndex) {
                // Moving down: insert index decreases by 1 because we remove source first
                newPos = insertIndex - 1;
            }
            // If moving up: insert index stays the same
        }
        // Reorder slides - handle same section vs different section
        const newSections = plan.sections.map((section, sIdx) => {
            if (sourceSectionId === sectionId && sIdx === sourceSectionIndex) {
                // Same section: remove and reinsert
                const newSlides = [...section.slides];
                const [removedSlide] = newSlides.splice(sourceSlideIndex, 1);
                // Adjust insert index if needed
                const finalInsertIndex = sourceSlideIndex < insertIndex ? insertIndex - 1 : insertIndex;
                newSlides.splice(finalInsertIndex, 0, removedSlide);
                return { ...section, slides: newSlides };
            }
            else if (sIdx === sourceSectionIndex) {
                // Remove from source section
                const newSlides = section.slides.filter(s => s.id !== draggedSlideId);
                return { ...section, slides: newSlides };
            }
            else if (sIdx === plan.sections.findIndex(s => s.id === sectionId)) {
                // Insert into target section
                const newSlides = [...section.slides];
                newSlides.splice(insertIndex, 0, sourceSlide);
                return { ...section, slides: newSlides };
            }
            return section;
        });
        const updatedPlan = {
            ...plan,
            sections: newSections,
            metadata: {
                ...plan.metadata,
                lastModified: Date.now(),
            },
        };
        // Track reordering operation if position changed
        const finalNewPos = sourceSectionId === sectionId ? newPos : insertIndex;
        // Use originalPos if available, otherwise fallback to source position
        const originalOrder = originalPos ? originalPos.order : sourceSlideIndex;
        const originalSectionId = originalPos ? originalPos.sectionId : sourceSectionId;
        // Calculate absolute slide numbers (across all sections) for display
        let newAbsoluteSlideNumber = 0;
        for (let i = 0; i < updatedPlan.sections.length; i++) {
            const section = updatedPlan.sections[i];
            if (section.id === sectionId) {
                const slideIndex = section.slides.findIndex(s => s.id === draggedSlideId);
                if (slideIndex !== -1) {
                    newAbsoluteSlideNumber = newAbsoluteSlideNumber + slideIndex + 1;
                    break;
                }
            }
            newAbsoluteSlideNumber += section.slides.length;
        }
        // Calculate original absolute slide number
        let originalAbsoluteSlideNumber = 0;
        for (let i = 0; i < plan.sections.length; i++) {
            const section = plan.sections[i];
            if (section.id === originalSectionId) {
                originalAbsoluteSlideNumber = originalAbsoluteSlideNumber + originalOrder + 1;
                break;
            }
            originalAbsoluteSlideNumber += section.slides.length;
        }
        // Track if position actually changed
        if (originalSectionId !== sectionId || originalOrder !== finalNewPos) {
            console.log('[PresentationPlanView] Tracking slide reorder:', {
                slideId: draggedSlideId,
                slideName: sourceSlide.name,
                from: { sectionId: originalSectionId, order: originalOrder, absolute: originalAbsoluteSlideNumber },
                to: { sectionId, order: finalNewPos, absolute: newAbsoluteSlideNumber },
            });
            addSlideOperation({
                operation: 'reordered',
                slideId: draggedSlideId,
                slideName: sourceSlide.name,
                sectionName: targetSection.name,
                slideNumber: newAbsoluteSlideNumber, // 1-indexed for display
                timestamp: Date.now(),
                previousPosition: originalAbsoluteSlideNumber - 1, // 0-indexed for tracking
                newPosition: newAbsoluteSlideNumber - 1, // 0-indexed for tracking
                source: 'ui', // UI-initiated: only for automated reminder, not shown in ChangeTracker
            });
            // Slide reorder only affects slide styling (yellow), NOT section styling
        }
        else {
            console.log('[PresentationPlanView] Slide position unchanged, not tracking reorder');
        }
        if (onPlanUpdate) {
            onPlanUpdate(updatedPlan);
        }
        setDraggedSlideId(null);
        setDragOverDropZone(null);
    }, [plan, draggedSlideId, originalSlideOrders, addSlideOperation, onPlanUpdate, setDraggedSlideId, setDragOverDropZone]);
    const handleSlideDrop = useCallback((e, _targetSlideId, _targetSectionId, _insertIndex) => {
        // Prevent drop on slide itself - only allow drop zones
        e.preventDefault();
        e.stopPropagation();
    }, []);
    // Handle accepting a single slide change
    const handleAcceptSlideChange = useCallback((slideId) => {
        if (!plan)
            return;
        const slide = findSlide(slideId);
        if (!slide)
            return;
        // Clean up flags for this slide
        const slideIdsToClean = new Set([slideId]);
        const updatedPlan = cleanupFlags(plan, undefined, slideIdsToClean);
        // Mark this plan update as coming from accept action to prevent re-tracking
        lastAcceptRejectPlanId.current = plan.id + '_' + (plan.version || 0);
        if (onPlanUpdate) {
            onPlanUpdate(updatedPlan);
        }
        // Clear the original position tracking for this slide since change is accepted
        setOriginalSlideOrders(prev => {
            const newMap = new Map(prev);
            newMap.delete(slideId);
            return newMap;
        });
        // Remove this slide's operation from the tracking
        const remainingOps = slideOperations.filter(op => op.slideId !== slideId);
        clearSlideOperations();
        // Re-add the remaining operations
        remainingOps.forEach(op => addSlideOperation(op));
    }, [plan, findSlide, onPlanUpdate, slideOperations, clearSlideOperations, addSlideOperation, setOriginalSlideOrders]);
    // Handle rejecting a single slide change
    const handleRejectSlideChange = useCallback((slideId, sectionId) => {
        if (!plan)
            return;
        const slide = findSlide(slideId);
        if (!slide)
            return;
        // Find section for the slide
        const section = plan.sections.find(s => s.id === sectionId);
        const sectionName = section?.name || 'Unknown Section';
        // Calculate slide number
        let slideNumber = 0;
        for (const sec of plan.sections) {
            for (const sl of sec.slides) {
                slideNumber++;
                if (sl.id === slideId)
                    break;
            }
            if (sec.id === sectionId)
                break;
        }
        let updatedPlan;
        // If slide is inserted, remove it and track rejection
        if (slide._pendingInsertion) {
            // Track: User rejected LLM's insertion
            addSlideOperation({
                operation: 'rejected_insertion',
                slideId: slideId,
                slideName: slide.name,
                sectionName: sectionName,
                slideNumber: slideNumber,
                timestamp: Date.now(),
                source: 'ui', // This is a user action
                rejectedOperation: 'inserted',
            });
            updatedPlan = {
                ...plan,
                sections: plan.sections.map(section => ({
                    ...section,
                    slides: section.slides.filter(s => s.id !== slideId),
                })),
                metadata: plan.metadata, // Don't update lastModified during reject
            };
            updatedPlan.slideCount = updatedPlan.sections.reduce((sum, s) => sum + s.slides.length, 0);
        }
        else if (slide._pendingUpdate) {
            // Track: User rejected LLM's update
            addSlideOperation({
                operation: 'rejected_update',
                slideId: slideId,
                slideName: slide.name,
                sectionName: sectionName,
                slideNumber: slideNumber,
                timestamp: Date.now(),
                source: 'ui',
                rejectedOperation: 'updated',
            });
            // Clear flags for updated slides
            updatedPlan = {
                ...plan,
                sections: plan.sections.map(section => {
                    if (section.id !== sectionId)
                        return section;
                    return {
                        ...section,
                        slides: section.slides.map(s => {
                            if (s.id !== slideId)
                                return s;
                            const { _pendingDeletion, _pendingUpdate, _pendingInsertion, ...cleanedSlide } = s;
                            return cleanedSlide;
                        }),
                    };
                }),
                metadata: plan.metadata, // Don't update lastModified during reject
            };
        }
        else if (slide._pendingDeletion) {
            // Track: User rejected LLM's deletion (user wants to keep the slide)
            addSlideOperation({
                operation: 'rejected_deletion',
                slideId: slideId,
                slideName: slide.name,
                sectionName: sectionName,
                slideNumber: slideNumber,
                timestamp: Date.now(),
                source: 'ui',
                rejectedOperation: 'deleted',
            });
            // Clear flags for deleted slides (keep the slide)
            updatedPlan = {
                ...plan,
                sections: plan.sections.map(section => {
                    if (section.id !== sectionId)
                        return section;
                    return {
                        ...section,
                        slides: section.slides.map(s => {
                            if (s.id !== slideId)
                                return s;
                            const { _pendingDeletion, _pendingUpdate, _pendingInsertion, ...cleanedSlide } = s;
                            return cleanedSlide;
                        }),
                    };
                }),
                metadata: plan.metadata, // Don't update lastModified during reject
            };
        }
        else {
            // No pending flags - just clear any remaining flags
            updatedPlan = {
                ...plan,
                sections: plan.sections.map(section => {
                    if (section.id !== sectionId)
                        return section;
                    return {
                        ...section,
                        slides: section.slides.map(s => {
                            if (s.id !== slideId)
                                return s;
                            const { _pendingDeletion, _pendingUpdate, _pendingInsertion, ...cleanedSlide } = s;
                            return cleanedSlide;
                        }),
                    };
                }),
                metadata: plan.metadata, // Don't update lastModified during reject
            };
        }
        // Handle reordered slides (still use operation-based tracking)
        const operation = getSlideOperation(slideId);
        if (operation && operation.operation === 'reordered' && operation.previousPosition !== undefined) {
            // Track: User rejected LLM's reorder
            addSlideOperation({
                operation: 'rejected_reorder',
                slideId: slideId,
                slideName: slide.name,
                sectionName: sectionName,
                slideNumber: slideNumber,
                timestamp: Date.now(),
                source: 'ui',
                rejectedOperation: 'reordered',
                previousPosition: operation.previousPosition,
                newPosition: operation.newPosition,
            });
            // Revert slide to original position
            const originalPos = originalSlideOrders.get(slideId);
            console.log('[PresentationPlanView] Rejecting slide reorder:', {
                slideId,
                operation,
                originalPos,
                hasOriginalPos: !!originalPos
            });
            if (originalPos) {
                // Find current position
                let currentSectionIndex = -1;
                let currentSlideIndex = -1;
                for (let i = 0; i < updatedPlan.sections.length; i++) {
                    const slideIndex = updatedPlan.sections[i].slides.findIndex(s => s.id === slideId);
                    if (slideIndex !== -1) {
                        currentSectionIndex = i;
                        currentSlideIndex = slideIndex;
                        break;
                    }
                }
                if (currentSectionIndex !== -1 && currentSlideIndex !== -1) {
                    const currentSection = updatedPlan.sections[currentSectionIndex];
                    const originalSection = updatedPlan.sections.find(s => s.id === originalPos.sectionId);
                    if (originalSection && (currentSectionIndex !== updatedPlan.sections.findIndex(s => s.id === originalPos.sectionId) || currentSlideIndex !== originalPos.order)) {
                        console.log('[PresentationPlanView] Reverting slide position:', {
                            from: { sectionIndex: currentSectionIndex, slideIndex: currentSlideIndex },
                            to: { sectionId: originalPos.sectionId, slideIndex: originalPos.order }
                        });
                        // Remove from current position
                        const newSections = updatedPlan.sections.map((section, sIdx) => {
                            if (sIdx === currentSectionIndex) {
                                return {
                                    ...section,
                                    slides: section.slides.filter(s => s.id !== slideId),
                                };
                            }
                            return section;
                        });
                        // Insert at original position
                        const originalSectionIndex = newSections.findIndex(s => s.id === originalPos.sectionId);
                        if (originalSectionIndex !== -1) {
                            const slideToMove = currentSection.slides[currentSlideIndex];
                            newSections[originalSectionIndex] = {
                                ...newSections[originalSectionIndex],
                                slides: [
                                    ...newSections[originalSectionIndex].slides.slice(0, originalPos.order),
                                    slideToMove,
                                    ...newSections[originalSectionIndex].slides.slice(originalPos.order),
                                ],
                            };
                        }
                        updatedPlan = {
                            ...updatedPlan,
                            sections: newSections,
                            metadata: updatedPlan.metadata, // Don't update lastModified during reject
                        };
                    }
                }
            }
        }
        updatedPlan.slideCount = updatedPlan.sections.reduce((sum, s) => sum + s.slides.length, 0);
        // Mark this plan update as coming from reject action to prevent re-tracking
        lastAcceptRejectPlanId.current = plan.id + '_' + (plan.version || 0);
        if (onPlanUpdate) {
            onPlanUpdate(updatedPlan);
        }
        // Clear the original position tracking for this slide since change is rejected
        setOriginalSlideOrders(prev => {
            const newMap = new Map(prev);
            newMap.delete(slideId);
            return newMap;
        });
        // Remove this slide's operation from the tracking
        const remainingOps = slideOperations.filter(op => op.slideId !== slideId);
        clearSlideOperations();
        // Re-add the remaining operations
        remainingOps.forEach(op => addSlideOperation(op));
    }, [plan, findSlide, getSlideOperation, originalSlideOrders, onPlanUpdate, slideOperations, clearSlideOperations, addSlideOperation, setOriginalSlideOrders]);
    const containerRef = useRef(null);
    const justFinishedSelecting = useRef(false);
    const lastAcceptRejectPlanId = useRef(null); // Track last plan from accept/reject to prevent re-tracking
    // Expose clear function to parent via ref
    React.useEffect(() => {
        if (onClearSectionTrackingRef) {
            onClearSectionTrackingRef.current = () => {
                setUpdatedSectionIds(new Set());
            };
        }
    }, [onClearSectionTrackingRef, setUpdatedSectionIds]);
    // Helper function to handle edit selection submission
    const handleEditSelectionSubmit = useCallback(async (prompt, _isSectionSelection) => {
        if (!prompt.trim())
            return;
        if (!conversationId) {
            console.error('[PresentationPlanView] No conversation initialized');
            return;
        }
        setProcessingEditSelection(true);
        // If sections are selected, convert to slide IDs
        let slidesToEdit = new Set(selectedSlides);
        if (selectedSections.size > 0) {
            const sectionsArray = Array.from(selectedSections);
            sectionsArray.forEach(sectionId => {
                const section = plan.sections.find(s => s.id === sectionId);
                if (section) {
                    section.slides.forEach(slide => slidesToEdit.add(slide.id));
                }
            });
        }
        // Temporarily set selected slides for context
        setSelectedSlides(slidesToEdit);
        // Set pending operation so we know to reset state when plan updates
        // Track multiple signals so we can detect when plan actually changes
        setPendingEditOperation({
            type: 'edit_selection',
            timestamp: Date.now(),
            planVersionAtStart: plan?.version || 0,
            planSectionsCountAtStart: plan?.sections?.length || 0,
            planLastModifiedAtStart: plan?.metadata?.lastModified || 0,
        });
        // Build context about selected items for the LLM
        const selectedSlidesList = Array.from(slidesToEdit);
        const contextInfo = selectedSlidesList.map(slideId => {
            // Find the slide and its parent section
            const slide = findSlide(slideId);
            if (!slide || !plan)
                return null;
            const section = plan.sections.find(s => s.slides.some(sl => sl.id === slideId));
            if (slide && section) {
                return `- "${slide.name}" in section "${section.name}"`;
            }
            return null;
        }).filter(Boolean).join('\n');
        // Enhance the prompt with selection context
        // IMPORTANT: Inline form edits should NOT invoke userInterview - just make the edit directly
        const enhancedPrompt = `Edit the following selected slides based on this instruction: "${prompt}"

Selected slides:
${contextInfo}

IMPORTANT: This is an inline edit request. Do NOT use the userInterview tool or ask clarifying questions.
Just use the edit_plan tool to make the requested changes directly to these specific slides.`;
        console.log('[PresentationPlanView] Sending edit selection via sendSilentMessage:', enhancedPrompt);
        try {
            // Send message using silent mode - doesn't add to chat UI
            // Plan updates will come through window.__registerPlanUpdateCallback
            await sendSilentMessage(enhancedPrompt);
        }
        catch (error) {
            console.error('[PresentationPlanView] Error updating slides:', error);
            // Reset state on error
            setEditingSelection(false);
            setEditSelectionPrompt("");
            setProcessingEditSelection(false);
            setPendingEditOperation(null);
        }
    }, [plan, selectedSlides, selectedSections, conversationId, setProcessingEditSelection, setSelectedSlides, findSlide, sendSilentMessage, setEditingSelection, setEditSelectionPrompt]);
    // Handler to delete selected slides - shows confirmation modal, then removes (UI-initiated)
    const handleDeleteSelectedSlides = useCallback(() => {
        if (!plan || selectedSlides.size === 0)
            return;
        setConfirmDialog({
            isOpen: true,
            title: 'Delete Slides?',
            message: `Are you sure you want to delete ${selectedSlides.size} selected slide(s)? This action cannot be undone.`,
            confirmLabel: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                setConfirmDialog(null);
                // Track deletions for automated reminder before removing (UI-initiated = source: 'ui')
                let slideNumber = 0;
                for (const sec of plan.sections) {
                    for (const slide of sec.slides) {
                        slideNumber++;
                        if (selectedSlides.has(slide.id)) {
                            addSlideOperation({
                                operation: 'deleted',
                                slideId: slide.id,
                                slideName: slide.name,
                                sectionName: sec.name,
                                slideNumber: slideNumber,
                                timestamp: Date.now(),
                                source: 'ui', // UI-initiated: only for automated reminder, not shown in ChangeTracker
                            });
                        }
                    }
                }
                const updatedPlan = {
                    ...plan,
                    sections: plan.sections.map(sec => ({
                        ...sec,
                        slides: sec.slides.filter(slide => !selectedSlides.has(slide.id)),
                    })),
                    metadata: { ...plan.metadata, lastModified: Date.now() },
                };
                updatedPlan.slideCount = updatedPlan.sections.reduce((sum, s) => sum + s.slides.length, 0);
                // Clear original position tracking for deleted slides
                setOriginalSlideOrders(prev => {
                    const newMap = new Map(prev);
                    selectedSlides.forEach(slideId => newMap.delete(slideId));
                    return newMap;
                });
                if (onPlanUpdate)
                    onPlanUpdate(updatedPlan);
                setSelectedSlides(new Set());
            },
        });
    }, [plan, selectedSlides, onPlanUpdate, setSelectedSlides, addSlideOperation, setOriginalSlideOrders]);
    // Wrapper for the helper function to inject selectedSlides
    const isFirstSelectedSlideDivider = useCallback((sectionId, beforeSlideIndex) => {
        return isFirstSelectedSlideDividerHelper(sectionId, beforeSlideIndex, selectedSlides);
    }, [selectedSlides]);
    // Handle adding a new slide to a section at a specific position
    const handleStartAddSlide = startAddSlide;
    const handleCancelAddSlide = cancelAddSlide;
    const handleSaveNewSlide = useCallback((sectionId, sectionName, insertIndex) => {
        if (!newSlideTitle.trim() || !plan) {
            return; // Don't save empty slide or if no plan
        }
        // Generate a simple ID for the new slide
        const newSlideId = `slide-${Date.now()}`;
        // Calculate the slide number (for display in reminder)
        // Sections are NOT slides, only count content slides
        let slideNumber = 0;
        for (const sec of plan.sections) {
            if (sec.id === sectionId) {
                slideNumber += insertIndex + 1; // Position where it will be inserted
                break;
            }
            slideNumber += sec.slides.length;
        }
        // Create the new slide object - NO pending flags for UI-initiated operations
        // UI changes apply immediately, only LLM changes get accept/reject UI
        const newSlide = {
            id: newSlideId,
            name: newSlideTitle,
            purpose: newSlidePurpose || undefined,
            order: insertIndex,
            // No _pendingInsertion flag - UI changes are immediate
        };
        // Create updated plan with the new slide inserted at the specified position
        const updatedPlan = {
            ...plan,
            sections: plan.sections.map(section => {
                if (section.id === sectionId) {
                    const newSlides = [...section.slides];
                    newSlides.splice(insertIndex, 0, newSlide); // Insert at specific position
                    return {
                        ...section,
                        slides: newSlides,
                    };
                }
                return section;
            }),
            metadata: {
                ...plan.metadata,
                lastModified: Date.now(),
            },
        };
        // Recalculate total slide count (only count content slides, NOT sections)
        let totalSlides = 0;
        for (const section of updatedPlan.sections) {
            totalSlides += section.slides.length;
        }
        updatedPlan.slideCount = totalSlides;
        // Track the added slide for automated reminder (UI-initiated = source: 'ui')
        addSlideOperation({
            operation: 'inserted',
            slideId: newSlideId,
            slideName: newSlideTitle,
            sectionName: sectionName,
            slideNumber: slideNumber,
            timestamp: Date.now(),
            source: 'ui', // UI-initiated: only for automated reminder, not shown in ChangeTracker
        });
        console.log('[PresentationPlanView] Slide added to plan:', {
            slideId: newSlideId,
            slideName: newSlideTitle,
            sectionName,
            slideNumber,
            totalSlides: updatedPlan.slideCount,
        });
        // Update the plan state
        if (onPlanUpdate) {
            onPlanUpdate(updatedPlan);
        }
        // Reset the form
        setAddingAtPosition(null);
        setNewSlideTitle("");
        setNewSlidePurpose("");
    }, [plan, newSlideTitle, newSlidePurpose, addSlideOperation, onPlanUpdate, setAddingAtPosition, setNewSlideTitle, setNewSlidePurpose]);
    // Handle editing a slide
    const handleStartEditSlide = startEditSlide;
    const handleCancelEditSlide = cancelEditSlide;
    const handleSaveEditSlide = useCallback((sectionId, sectionName, slideId, oldTitle) => {
        if (!editSlideTitle.trim() || !plan) {
            return;
        }
        // Get the current slide to compare values
        const currentSlide = findSlide(slideId);
        if (!currentSlide) {
            return;
        }
        // Check if anything actually changed
        const titleChanged = oldTitle.trim() !== editSlideTitle.trim();
        const purposeChanged = (currentSlide.purpose || "").trim() !== (editSlidePurpose || "").trim();
        // If nothing changed, just cancel the edit without tracking
        if (!titleChanged && !purposeChanged) {
            handleCancelEditSlide();
            return;
        }
        // Calculate slide number for the edited slide
        let slideNumber = 0;
        let found = false;
        for (const sec of plan.sections) {
            for (const slide of sec.slides) {
                slideNumber++;
                if (slide.id === slideId) {
                    found = true;
                    break;
                }
            }
            if (found)
                break;
        }
        // Create updated plan with the edited slide - NO pending flags for UI-initiated operations
        // UI changes apply immediately, only LLM changes get accept/reject UI
        const updatedPlan = {
            ...plan,
            sections: plan.sections.map(section => {
                if (section.id === sectionId) {
                    return {
                        ...section,
                        slides: section.slides.map(slide => slide.id === slideId
                            ? { ...slide, name: editSlideTitle, purpose: editSlidePurpose || undefined }
                            : slide),
                    };
                }
                return section;
            }),
            metadata: {
                ...plan.metadata,
                lastModified: Date.now(),
            },
        };
        // Track the update for automated reminder (UI-initiated = source: 'ui')
        addSlideOperation({
            operation: 'updated',
            slideId: slideId,
            slideName: editSlideTitle,
            sectionName: sectionName,
            slideNumber: slideNumber,
            timestamp: Date.now(),
            previousName: titleChanged ? oldTitle : undefined,
            source: 'ui', // UI-initiated: only for automated reminder, not shown in ChangeTracker
        });
        console.log('[PresentationPlanView] Slide updated:', {
            slideId,
            newTitle: editSlideTitle,
            oldTitle,
            sectionName,
            titleChanged,
            purposeChanged,
        });
        // Update the plan state
        if (onPlanUpdate) {
            onPlanUpdate(updatedPlan);
        }
        // Reset edit state
        handleCancelEditSlide();
    }, [plan, editSlideTitle, editSlidePurpose, findSlide, handleCancelEditSlide, addSlideOperation, onPlanUpdate]);
    // Handle adding a new section
    const handleStartAddSection = startAddSection;
    const handleCancelAddSection = cancelAddSection;
    const handleSaveNewSection = useCallback(async () => {
        if (!newSectionDescription.trim() || !plan)
            return;
        if (!conversationId) {
            console.error('[PresentationPlanView] No conversation initialized');
            return;
        }
        // Show thinking state (keep the same section, just change state)
        setCreatingSectionAtIndex(addingSectionAfterIndex);
        // Set pending operation so we know to reset state when plan updates
        // Track multiple signals so we can detect when plan actually changes
        setPendingEditOperation({
            type: 'create_section',
            timestamp: Date.now(),
            planVersionAtStart: plan?.version || 0,
            planSectionsCountAtStart: plan?.sections?.length || 0,
            planLastModifiedAtStart: plan?.metadata?.lastModified || 0,
        });
        try {
            // Create a message asking to add the section after the current one
            const sectionIndex = addingSectionAfterIndex ?? -1;
            const afterSectionName = sectionIndex >= 0 && sectionIndex < plan.sections.length
                ? plan.sections[sectionIndex].name
                : null;
            // Calculate appropriate number of slides for the new section
            const suggestedSlideCount = Math.max(2, Math.ceil(plan.slideCount / (plan.sections.length + 1)));
            // IMPORTANT: Inline form operations should NOT invoke userInterview - just execute directly
            const message = afterSectionName
                ? `I need you to add a new section about "${newSectionDescription}" after the "${afterSectionName}" section.

IMPORTANT: This is an inline form request. Do NOT use the userInterview tool or ask clarifying questions. Execute the edit_plan tool directly.

CRITICAL REQUIREMENTS - YOU MUST FOLLOW THIS EXACT PATTERN:
1. In the add_section operation, specify a temporary sectionId like "new_section_1"
2. Then IMMEDIATELY use add_slide operations with that SAME sectionId to add AT LEAST ${suggestedSlideCount} slides
3. A section without slides is INVALID and will be rejected
4. Each slide MUST have:
   - A specific, detailed title (name)
   - A clear purpose describing what content it will contain

REQUIRED OPERATION SEQUENCE:
{ type: "add_section", sectionId: "new_section_1", section: { name: "${newSectionDescription}", purpose: "..." }, after: "${plan.sections[sectionIndex]?.id}" }
{ type: "add_slide", sectionId: "new_section_1", slide: { name: "Slide 1 Title", purpose: "Detailed purpose..." } }
{ type: "add_slide", sectionId: "new_section_1", slide: { name: "Slide 2 Title", purpose: "Detailed purpose..." } }
... (${suggestedSlideCount} slides minimum)

The slides should comprehensively cover: ${newSectionDescription}

DO NOT create an empty section. The sectionId "new_section_1" is a temporary ID you specify so you can reference it in add_slide operations.`
                : `I need you to add a new section about "${newSectionDescription}".

IMPORTANT: This is an inline form request. Do NOT use the userInterview tool or ask clarifying questions. Execute the edit_plan tool directly.

CRITICAL REQUIREMENTS - YOU MUST FOLLOW THIS EXACT PATTERN:
1. In the add_section operation, specify a temporary sectionId like "new_section_1"
2. Then IMMEDIATELY use add_slide operations with that SAME sectionId to add AT LEAST ${suggestedSlideCount} slides
3. A section without slides is INVALID and will be rejected
4. Each slide MUST have:
   - A specific, detailed title (name)
   - A clear purpose describing what content it will contain

REQUIRED OPERATION SEQUENCE:
{ type: "add_section", sectionId: "new_section_1", section: { name: "${newSectionDescription}", purpose: "..." } }
{ type: "add_slide", sectionId: "new_section_1", slide: { name: "Slide 1 Title", purpose: "Detailed purpose..." } }
{ type: "add_slide", sectionId: "new_section_1", slide: { name: "Slide 2 Title", purpose: "Detailed purpose..." } }
... (${suggestedSlideCount} slides minimum)

The slides should comprehensively cover: ${newSectionDescription}

DO NOT create an empty section. The sectionId "new_section_1" is a temporary ID you specify so you can reference it in add_slide operations.`;
            console.log('[PresentationPlanView] Sending section creation request via sendSilentMessage:', message);
            // Send message using silent mode - doesn't add to chat UI
            // Plan updates will come through window.__registerPlanUpdateCallback
            await sendSilentMessage(message);
        }
        catch (error) {
            console.error('[PresentationPlanView] Error in handleSaveNewSection:', error);
            console.warn('Failed to create section. Please try again.');
            // Reset state on error
            setCreatingSectionAtIndex(null);
            setAddingSectionAfterIndex(null);
            setNewSectionDescription("");
            setPendingEditOperation(null);
        }
    }, [plan, newSectionDescription, addingSectionAfterIndex, conversationId, sendSilentMessage, setCreatingSectionAtIndex, setAddingSectionAfterIndex, setNewSectionDescription]);
    const handleStopSectionCreation = stopSectionCreation;
    // Multi-select handlers
    const handleMouseDown = (e) => {
        const target = e.target;
        // Don't start selection if clicking on buttons, inputs, or interactive elements
        if (target.closest('button') || target.closest('input') || target.closest('textarea')) {
            return;
        }
        // Don't start selection if clicking on a slide (individual click handles that)
        if (target.closest('[data-slide-id]')) {
            return;
        }
        // Don't start selection if clicking on a section (allows drag to work)
        if (target.closest('[data-section-id]')) {
            return;
        }
        // Prevent text selection while dragging
        e.preventDefault();
        // Start selection on container background areas
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
            setIsSelecting(true);
            setSelectionStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
            setSelectionEnd({ x: e.clientX - rect.left, y: e.clientY - rect.top });
            // Clear selection if not holding Ctrl/Cmd
            if (!e.ctrlKey && !e.metaKey) {
                setSelectedSlides(new Set());
            }
        }
    };
    const handleMouseMove = (e) => {
        if (isSelecting && selectionStart && containerRef.current) {
            // Prevent text selection
            e.preventDefault();
            const rect = containerRef.current.getBoundingClientRect();
            setSelectionEnd({ x: e.clientX - rect.left, y: e.clientY - rect.top });
            // Calculate which slides/sections are in the selection rectangle
            const selectionRect = getSelectionRect();
            const newSelectedSlides = new Set();
            const newSelectedSections = new Set();
            // Check each section header to see if it's being selected
            document.querySelectorAll('[data-section-id]').forEach((element) => {
                const sectionId = element.getAttribute('data-section-id');
                if (sectionId) {
                    // Find the section header button within this section
                    const sectionHeader = element.querySelector('button[aria-label^="Toggle"]');
                    if (sectionHeader) {
                        const headerRect = sectionHeader.getBoundingClientRect();
                        const containerRect = containerRef.current.getBoundingClientRect();
                        // Convert to container-relative coordinates
                        const headerRelativeRect = {
                            left: headerRect.left - containerRect.left,
                            top: headerRect.top - containerRect.top,
                            right: headerRect.right - containerRect.left,
                            bottom: headerRect.bottom - containerRect.top,
                        };
                        // Check if rectangles intersect
                        if (rectanglesIntersect(selectionRect, headerRelativeRect)) {
                            newSelectedSections.add(sectionId);
                        }
                    }
                }
            });
            // Only check for slide selection if no sections are being selected
            if (newSelectedSections.size === 0) {
                // Check each slide element
                document.querySelectorAll('[data-slide-id]').forEach((element) => {
                    const slideId = element.getAttribute('data-slide-id');
                    if (slideId) {
                        const slideRect = element.getBoundingClientRect();
                        const containerRect = containerRef.current.getBoundingClientRect();
                        // Convert to container-relative coordinates
                        const slideRelativeRect = {
                            left: slideRect.left - containerRect.left,
                            top: slideRect.top - containerRect.top,
                            right: slideRect.right - containerRect.left,
                            bottom: slideRect.bottom - containerRect.top,
                        };
                        // Check if rectangles intersect
                        if (rectanglesIntersect(selectionRect, slideRelativeRect)) {
                            newSelectedSlides.add(slideId);
                        }
                    }
                });
            }
            // If holding Ctrl/Cmd, merge with existing selection
            if (e.ctrlKey || e.metaKey) {
                setSelectedSlides(prev => new Set([...prev, ...newSelectedSlides]));
                setSelectedSections(prev => new Set([...prev, ...newSelectedSections]));
            }
            else {
                setSelectedSlides(newSelectedSlides);
                setSelectedSections(newSelectedSections);
            }
        }
    };
    const handleMouseUp = () => {
        if (isSelecting) {
            // Mark that we just finished selecting to prevent onClick from clearing selection
            justFinishedSelecting.current = true;
            setTimeout(() => {
                justFinishedSelecting.current = false;
            }, 100); // Small delay to prevent click event from clearing
        }
        setIsSelecting(false);
        setSelectionStart(null);
        setSelectionEnd(null);
    };
    const getSelectionRect = () => {
        if (!selectionStart || !selectionEnd)
            return { left: 0, top: 0, right: 0, bottom: 0 };
        return {
            left: Math.min(selectionStart.x, selectionEnd.x),
            top: Math.min(selectionStart.y, selectionEnd.y),
            right: Math.max(selectionStart.x, selectionEnd.x),
            bottom: Math.max(selectionStart.y, selectionEnd.y),
        };
    };
    const rectanglesIntersect = (rect1, rect2) => {
        return !(rect1.right < rect2.left ||
            rect1.left > rect2.right ||
            rect1.bottom < rect2.top ||
            rect1.top > rect2.bottom);
    };
    // Add global mouse up listener and prevent text selection while dragging
    useEffect(() => {
        const handleGlobalMouseUp = () => {
            if (isSelecting) {
                handleMouseUp();
            }
        };
        // Prevent text selection globally when selecting (but keep pointer cursor)
        if (isSelecting) {
            document.body.style.userSelect = 'none';
        }
        else {
            document.body.style.userSelect = '';
        }
        window.addEventListener('mouseup', handleGlobalMouseUp);
        return () => {
            window.removeEventListener('mouseup', handleGlobalMouseUp);
            document.body.style.userSelect = '';
        };
    }, [isSelecting]);
    // Handle individual slide click
    const handleSlideClick = useCallback((slideId, e) => {
        // Don't handle click if we just finished dragging
        if (draggedSlideId) {
            return;
        }
        e.stopPropagation();
        if (e.ctrlKey || e.metaKey) {
            // Toggle selection with Ctrl/Cmd
            setSelectedSlides(prev => {
                const newSet = new Set(prev);
                if (newSet.has(slideId)) {
                    newSet.delete(slideId);
                }
                else {
                    newSet.add(slideId);
                }
                return newSet;
            });
        }
        else if (e.shiftKey && selectedSlides.size > 0) {
            // Range selection with Shift (future enhancement)
            // For now, just select this slide
            setSelectedSlides(new Set([slideId]));
        }
        else {
            // Single selection
            setSelectedSlides(new Set([slideId]));
        }
    }, [draggedSlideId, selectedSlides, setSelectedSlides]);
    // Clear selection when clicking on container background (but not when dragging)
    const handleContainerClick = (e) => {
        // Don't clear if we just finished a drag selection
        if (isSelecting || justFinishedSelecting.current) {
            return;
        }
        const target = e.target;
        // Check if clicked on elements that should NOT clear selection
        const clickedInsideSlide = target.closest('[data-slide-id]');
        const clickedOnInput = target.closest('input');
        const clickedOnTextarea = target.closest('textarea');
        // Special buttons that SHOULD allow clearing (even though they're buttons)
        const clickedOnSectionHeader = target.closest('[data-section-header]');
        const clickedOnChangesAction = target.closest('[data-changes-action]');
        // Check for regular buttons (edit, delete, etc.) that should NOT clear
        const clickedOnButton = target.closest('button');
        const clickedOnPreservingButton = clickedOnButton && !clickedOnSectionHeader && !clickedOnChangesAction;
        // Clear selection if:
        // - NOT inside a slide
        // - NOT on input/textarea
        // - NOT on a preserving button (edit, delete, add, etc.)
        if (!clickedInsideSlide && !clickedOnInput && !clickedOnTextarea && !clickedOnPreservingButton) {
            setSelectedSlides(new Set());
            setSelectedSections(new Set());
            setEditingSelection(false);
            setEditSelectionPrompt("");
        }
    };
    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Escape to clear selection or cancel editing
            if (e.key === 'Escape') {
                if (editingSelection) {
                    setEditingSelection(false);
                    setEditSelectionPrompt("");
                    setSelectedSections(new Set());
                    e.preventDefault();
                }
                else if (selectedSlides.size > 0 || selectedSections.size > 0) {
                    setSelectedSlides(new Set());
                    setSelectedSections(new Set());
                    e.preventDefault();
                }
            }
            // Ctrl+A to select all slides
            if ((e.ctrlKey || e.metaKey) && e.key === 'a' && plan) {
                const allSlideIds = plan.sections.flatMap(section => section.slides.map(slide => slide.id));
                setSelectedSlides(new Set(allSlideIds));
                e.preventDefault();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedSlides, selectedSections, editingSelection, plan]);
    // Handle deleting a slide - immediately removes the slide (UI-initiated deletion)
    // Accept/reject only appears for LLM-initiated changes
    const handleDeleteSlide = useCallback((sectionId, sectionName, slideId, slideName) => {
        if (!plan)
            return;
        // Calculate slide number before deletion
        let slideNumber = 0;
        let found = false;
        for (const sec of plan.sections) {
            for (const slide of sec.slides) {
                slideNumber++;
                if (slide.id === slideId) {
                    found = true;
                    break;
                }
            }
            if (found)
                break;
        }
        // Create updated plan with the slide immediately removed (not pending)
        const updatedPlan = {
            ...plan,
            sections: plan.sections.map(section => {
                if (section.id === sectionId) {
                    return {
                        ...section,
                        slides: section.slides.filter(slide => slide.id !== slideId),
                    };
                }
                return section;
            }),
            slideCount: plan.slideCount - 1,
            metadata: {
                ...plan.metadata,
                lastModified: Date.now(),
            },
        };
        // Track the deletion for automated reminder (UI-initiated = source: 'ui')
        addSlideOperation({
            operation: 'deleted',
            slideId: slideId,
            slideName: slideName,
            sectionName: sectionName,
            slideNumber: slideNumber,
            timestamp: Date.now(),
            source: 'ui', // UI-initiated: only for automated reminder, not shown in ChangeTracker
        });
        console.log('[PresentationPlanView] Slide deleted immediately:', {
            slideId,
            slideName,
            sectionName,
        });
        // Clear the original position tracking for this slide since it's deleted
        setOriginalSlideOrders(prev => {
            const newMap = new Map(prev);
            newMap.delete(slideId);
            return newMap;
        });
        // Update the plan state
        if (onPlanUpdate) {
            onPlanUpdate(updatedPlan);
        }
    }, [plan, addSlideOperation, onPlanUpdate, setOriginalSlideOrders]);
    if (!plan) {
        return (_jsx("div", { className: "flex-1 flex items-center justify-center", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "w-12 h-12 border-4 border-primary-purple-70 border-t-transparent rounded-full animate-spin mx-auto mb-4" }), _jsx("p", { className: "text-gray-600", children: "Creating your presentation plan..." })] }) }));
    }
    return (_jsxs(_Fragment, { children: [isSelecting && (_jsx("style", { dangerouslySetInnerHTML: {
                    __html: `
            * {
              -webkit-user-select: none !important;
              -moz-user-select: none !important;
              -ms-user-select: none !important;
              user-select: none !important;
            }
          `
                } })), _jsxs("div", { ref: containerRef, className: "relative min-h-full", onMouseDown: handleMouseDown, onMouseMove: handleMouseMove, onClick: handleContainerClick, "data-selection-container": true, children: [isSelecting && selectionStart && selectionEnd && (_jsx("div", { className: "absolute pointer-events-none z-50 rounded-sm", style: {
                            left: `${Math.min(selectionStart.x, selectionEnd.x)}px`,
                            top: `${Math.min(selectionStart.y, selectionEnd.y)}px`,
                            width: `${Math.abs(selectionEnd.x - selectionStart.x)}px`,
                            height: `${Math.abs(selectionEnd.y - selectionStart.y)}px`,
                            border: '1px solid #bfb9f1',
                            backgroundColor: 'rgba(212, 208, 246, 0.2)',
                            opacity: '0.8',
                        } })), _jsxs("div", { className: "p-4 min-h-screen", onClick: handleContainerClick, children: [_jsxs("div", { className: "flex items-start gap-3 self-stretch px-2 mb-2", children: [_jsx("h1", { className: "flex-1 font-bold text-[15px] leading-[1.2] text-gray-990", children: planDescription || 'Presentation Plan' }), _jsxs("div", { className: "flex items-center gap-2 shrink-0", children: [onQueueBuild && (_jsx("button", { onClick: onQueueBuild, disabled: isBuildingPlan || !plan || !currentPlanId, className: "px-3 py-1.5 rounded-lg border border-gray-300 bg-gray-100 text-[13px] font-bold text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer", children: "Queue Up" })), onStartBuildImmediately ? (_jsx(AIPrimaryButton, { width: "auto", height: "auto", padding: "6px 10px", onClick: onStartBuildImmediately, disabled: isBuildingPlan || !plan || !currentPlanId, children: isBuildingPlan ? 'Building...' : 'Start Building' })) : onBuildPlan && (_jsx(AIPrimaryButton, { width: "auto", height: "auto", padding: "6px 10px", onClick: onBuildPlan, disabled: isBuildingPlan || !plan || !currentPlanId, children: isBuildingPlan ? 'Building...' : 'Generate Slides' }))] })] }), _jsx("div", { className: "h-px w-full bg-gray-300 px-2" }), _jsxs("div", { className: "flex gap-2 items-start p-2 rounded-lg bg-white", children: [_jsx("div", { className: "bg-gray-100 border border-purple-sparkle-soft border-solid flex gap-[2px] items-center justify-center px-3 py-px rounded shrink-0 w-5", children: _jsx("span", { className: "bg-clip-text font-normal leading-normal text-[12px] text-purple-sparkle", children: "C" }) }), _jsxs("div", { className: "flex flex-col gap-1 flex-1", children: [_jsx("span", { className: "font-bold text-[13px] leading-normal text-gray-990", children: "Cover Page" }), _jsx("span", { className: "text-[12px] leading-normal text-gray-600", children: "Title, author, date" })] })] }), draggedSectionId && (_jsx("div", { className: "flex items-center justify-center w-full my-2 relative min-h-[24px]", onDragOver: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSectionDropZoneDragOver(e, 0);
                                }, onDragLeave: (e) => handleSectionDropZoneDragLeave(e), onDrop: (e) => handleSectionDropZoneDrop(e, 0), children: _jsx("div", { className: `absolute inset-0 flex items-center px-2 pointer-events-none z-0 transition-opacity ${dragOverSectionDropZone === 0 ? 'opacity-100' : 'opacity-0'}`, children: _jsx("div", { className: `h-1 w-full rounded transition-colors ${dragOverSectionDropZone === 0 ? 'bg-yellow-50' : 'bg-gray-300'}` }) }) })), plan && (_jsx(SectionList, { plan: plan, expandedSections: expandedSections, onToggleSection: onToggleSection, onPlanUpdate: onPlanUpdate, onAcceptSectionChanges: handleAcceptSectionChanges, onRejectSectionChanges: handleRejectSectionChanges, onAcceptSlideChange: handleAcceptSlideChange, onRejectSlideChange: handleRejectSlideChange, onStartEditSlide: handleStartEditSlide, onDeleteSlide: handleDeleteSlide, onSaveNewSlide: handleSaveNewSlide, onSaveEditSlide: handleSaveEditSlide, onSaveNewSection: handleSaveNewSection, onEditSelectionSubmit: handleEditSelectionSubmit, onDeleteSelectedSlides: handleDeleteSelectedSlides, handleSectionDragStart: handleSectionDragStart, handleSectionDragEnd: handleSectionDragEnd, handleSectionDragOver: handleSectionDragOver, handleSectionDropZoneDragOver: handleSectionDropZoneDragOver, handleSectionDropZoneDragLeave: handleSectionDropZoneDragLeave, handleSectionDropZoneDrop: handleSectionDropZoneDrop, handleDropZoneDragOver: handleDropZoneDragOver, handleDropZoneDragLeave: handleDropZoneDragLeave, handleDropZoneDrop: handleDropZoneDrop, handleSlideDragStart: handleSlideDragStart, handleSlideDragEnd: handleSlideDragEnd, handleSlideClick: handleSlideClick }))] })] }), confirmDialog && (_jsx(ConfirmationDialog, { isOpen: confirmDialog.isOpen, title: confirmDialog.title, message: confirmDialog.message, confirmLabel: confirmDialog.confirmLabel, variant: confirmDialog.variant, onConfirm: confirmDialog.onConfirm, onCancel: () => setConfirmDialog(null) }))] }));
};
export default PresentationPlanView;
